<template>
  <Card v-if="visible" class="mb-4 gap-2 py-3" data-testid="dashboard-quickstart-card">
    <div class="border-b border-gray-200 px-4 pb-2">
      <div class="flex items-center justify-between gap-4">
        <CardTitle>Get started</CardTitle>
        <div v-if="!celebrate" class="relative h-6 w-32 overflow-hidden rounded-full bg-slate-100">
          <div class="absolute inset-0 bg-gradient-to-r from-violet-500 to-emerald-500" />
          <div
            class="absolute inset-y-0 right-0 bg-slate-100 transition-[width] duration-500 ease-out"
            :style="{ width: `${100 - progressPct}%` }"
          />
          <div class="absolute inset-0 flex items-center justify-center">
            <span class="text-xs font-semibold text-slate-700">{{ completedCount }} of {{ totalCount }}</span>
          </div>
          <div
            class="absolute inset-0 flex items-center justify-center transition-[clip-path] duration-500 ease-out"
            :style="{ clipPath: `inset(0 ${100 - progressPct}% 0 0)` }"
          >
            <span class="text-xs font-semibold text-white">{{ completedCount }} of {{ totalCount }}</span>
          </div>
        </div>
      </div>
    </div>

    <CardContent class="px-0">
      <div v-if="celebrate" class="flex flex-col items-center px-6 py-8 text-center">
        <div class="text-4xl">🎉</div>
        <p class="mt-3 text-sm font-medium text-slate-900">You're all set up</p>
        <p class="mt-1 max-w-sm text-sm text-slate-500">
          Nice work — you've finished the Humaniz setup checklist. You can dismiss this card now.
        </p>
        <Button size="sm" variant="outline" class="mt-4" @click="dismiss">Dismiss</Button>
      </div>

      <template v-else>
        <DashboardQuickstartStepRow
          :step="orderedSteps[0]"
          :completed="orderedSteps[0].completed"
          :disabled="isButtonDisabled(orderedSteps[0])"
        />

        <div v-if="!isExpanded && orderedSteps.length > 1" class="relative">
          <DashboardQuickstartStepRow
            :step="orderedSteps[1]"
            :completed="orderedSteps[1].completed"
            :disabled="isButtonDisabled(orderedSteps[1])"
          />
          <div
            class="pointer-events-none absolute inset-x-0 bottom-0 h-full bg-gradient-to-t from-white via-white/80 to-transparent"
          />
        </div>

        <template v-if="isExpanded">
          <DashboardQuickstartStepRow
            v-for="step in orderedSteps.slice(1)"
            :key="step.key"
            :step="step"
            :completed="step.completed"
            :disabled="isButtonDisabled(step)"
          />
        </template>

        <div v-if="orderedSteps.length > 1" class="flex justify-center pt-2">
          <Button variant="link" size="sm" @click="isExpanded = !isExpanded">
            {{ isExpanded ? 'Show less' : `View all (${orderedSteps.length})` }}
          </Button>
        </div>
      </template>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import DashboardQuickstartStepRow from './DashboardQuickstartStepRow.vue';
import { initialQuickstartSteps, type QuickstartStep } from '../dashboard_quickstart/quickstartSteps';
import { useCurrentUserStore } from '@/stores/currentUser';

const currentUserStore = useCurrentUserStore();

const steps = reactive<QuickstartStep[]>(initialQuickstartSteps.map(s => ({ ...s })));
const loaded = ref(false);
const isExpanded = ref(false);
const dismissed = ref(false);

const dismissKey = computed(() => {
  const id = currentUserStore.currentUser?.id ?? 'anonymous';
  return `dashboard_quickstart_dismissed_v1_${id}`;
});

const orderedSteps = computed<QuickstartStep[]>(() => {
  const incomplete = steps.filter(s => !s.completed);
  const complete = steps.filter(s => s.completed);
  return [...incomplete, ...complete];
});

const incompleteKeys = computed(() => steps.filter(s => !s.completed).map(s => s.key));

const totalCount = computed(() => steps.length);
const completedCount = computed(() => steps.filter(s => s.completed).length);
const progressPct = computed(() =>
  totalCount.value === 0 ? 0 : Math.round((completedCount.value / totalCount.value) * 100)
);

const allComplete = computed(() => loaded.value && completedCount.value === totalCount.value);
const celebrate = computed(() => allComplete.value && !dismissed.value);
const visible = computed(() => loaded.value && !(allComplete.value && dismissed.value));

const isButtonDisabled = (step: QuickstartStep) => {
  if (!step.disabled) return false;
  // Same gating as the legacy DashboardQuickstart: kickoff call locked until required steps done
  if (incompleteKeys.value.includes('jobs_setup')) return true;
  if (incompleteKeys.value.includes('phone_number_setup')) return true;
  if (incompleteKeys.value.includes('recruiting_social_ads_setup')) return true;
  return false;
};

const syncCompletedSteps = async () => {
  const res = await fetch('/api/setup/1');
  if (!res.ok) return;
  const data = await res.json();
  for (const setupKey of Object.keys(data.steps)) {
    for (const step of steps) {
      if (setupKey === step.key) {
        step.completed = data.steps[setupKey];
      }
    }
  }
};

const dismiss = () => {
  try {
    window.localStorage.setItem(dismissKey.value, new Date().toISOString());
  } catch {
    // localStorage unavailable — fall back to in-memory dismissal for this session
  }
  dismissed.value = true;
};

onMounted(async () => {
  try {
    if (window.localStorage.getItem(dismissKey.value)) {
      dismissed.value = true;
    }
  } catch {
    // ignore
  }
  await syncCompletedSteps();
  loaded.value = true;
});
</script>
